import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/spa";
import { useLang, LANGS } from "@/i18n/LanguageContext";

const hrefs = [
  ["philosophy", "#philosophy"],
  ["services", "#services"],
  ["offers", "#offers"],
  ["gifts", "#gifts"],
  ["team", "#team"],
  ["book", "#book"],
  ["visit", "#visit"],
];

const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-switch-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-2 py-1 text-[10px] tracking-[0.1em] uppercase border transition-colors duration-300 ${
            lang === l.code ? "border-[#c5a059] text-[#c5a059]" : "border-white/10 text-[#8a9690] hover:text-[#f0f4f2]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [annIndex, setAnnIndex] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setAnnIndex((i) => (i + 1) % t.announcements.length), 4000);
    return () => clearInterval(timer);
  }, [t]);

  return (
    <>
      <div
        data-testid="announcement-bar"
        className="fixed top-0 left-0 right-0 z-[60] bg-[#c5a059] text-[#050706] text-[11px] sm:text-xs tracking-[0.25em] uppercase text-center py-2 px-4 font-medium h-8 flex items-center justify-center overflow-hidden"
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={annIndex + t.announcements[annIndex]}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="block truncate"
          >
            {t.announcements[annIndex]}
          </motion.span>
        </AnimatePresence>
      </div>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-8 left-0 right-0 z-50 transition-colors duration-500 border-b ${
          scrolled
            ? "bg-[#050706]/85 backdrop-blur-xl border-white/10"
            : "bg-[#050706]/75 backdrop-blur-md border-white/5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 sm:h-20">
          <a href="#top" data-testid="nav-logo" className="group flex items-baseline gap-2">
            <span className="font-serif-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#f0f4f2]">Elite</span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Spa</span>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {hrefs.map(([key, href]) => (
              <a
                key={href}
                href={href}
                data-testid={`nav-link-${key}`}
                className="relative text-xs tracking-[0.15em] uppercase text-[#8a9690] hover:text-[#f0f4f2] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#c5a059] after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {t.nav[key]}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <LangSwitch />
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button"
              className="group flex items-center gap-2.5 border border-[#c5a059]/50 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#050706] px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors duration-300"
            >
              <Phone size={13} className="group-hover:animate-pulse" />
              <span>{PHONE_DISPLAY}</span>
            </a>
          </div>
          <div className="flex lg:hidden items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button-mobile"
              className="flex items-center gap-2 border border-[#c5a059]/50 text-[#c5a059] px-4 py-2.5 text-xs tracking-[0.15em] uppercase"
            >
              <Phone size={13} /> {t.nav.call}
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="w-11 h-11 flex items-center justify-center border border-white/20 text-[#f0f4f2] hover:border-[#c5a059] hover:text-[#c5a059] transition-colors duration-300"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-[#050706]/95 backdrop-blur-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
              <span className="font-serif-display text-2xl font-semibold text-[#f0f4f2]">
                Elite <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059] font-sans">Spa</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="w-11 h-11 flex items-center justify-center border border-white/20 text-[#f0f4f2] hover:border-[#c5a059] hover:text-[#c5a059] transition-colors duration-300"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {hrefs.map(([key, href], i) => (
                <motion.a
                  key={href}
                  href={href}
                  data-testid={`mobile-nav-link-${key}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setMenuOpen(false)}
                  className="group flex items-baseline gap-4 py-3 border-b border-white/5"
                >
                  <span className="text-xs text-[#c5a059] font-serif-display italic">0{i + 1}</span>
                  <span className="font-serif-display text-3xl text-[#f0f4f2] group-hover:text-[#c5a059] transition-colors duration-300">
                    {t.nav[key]}
                  </span>
                </motion.a>
              ))}
              <LangSwitch className="mt-6" />
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="p-8 border-t border-white/10"
            >
              <a
                href={PHONE_TEL}
                data-testid="mobile-menu-call-button"
                className="flex items-center justify-center gap-3 bg-[#c5a059] text-[#050706] py-4 text-xs tracking-[0.2em] uppercase font-medium"
              >
                <Phone size={14} /> {t.nav.call} {PHONE_DISPLAY}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
